import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import wordmarkBlack from '../../Assets/mf-wordmark-black.png';
import wordmarkWhite from '../../Assets/mf-wordmark-white.png';
import {
  APP_VERSION,
  MF_PRIVACY_URL,
  MF_SUPPORT_URL,
  MF_TERMS_URL,
} from '../../utils/legalLinks';
import '../../Pages/IntegrationGuideDocs.css';

const DOCS_SUPPORT_EMAIL = 'support@magicfurnish.com';
const DOCS_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=GFS+Didot&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@300;400;500;600&display=swap';

const MOBILE_NAV_MQ = '(max-width: 900px)';
const SCROLL_SPY_OFFSET = 80;

const THEME_STORAGE_KEY = 'mf-docs-theme';
const THEMES = ['light', 'dark'];

// Remember an explicit choice; otherwise seed from the OS preference on first
// visit so the initial paint still matches the user's system.
const readInitialTheme = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (THEMES.includes(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    /* matchMedia unavailable */
  }
  return 'light';
};

/**
 * Shared chrome for MagicFurnish guides: top bar, sidebar, theme, scroll spy,
 * heading links, and footer. Page files supply the nav and the article.
 */
const DocsGuide = ({ documentTitle, navGroups, lastUpdated, showNav = true, children }) => {
  const navSectionIds = useMemo(
    () => navGroups.flatMap((group) => group.items.map((item) => item.id)),
    [navGroups],
  );
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [theme, setTheme] = useState(readInitialTheme);

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* storage unavailable */
    }
  }, [theme]);

  const scrollToTop = useCallback(() => {
    closeNav();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [closeNav]);

  useEffect(() => {
    document.documentElement.classList.add('mf-docs-page');
    return () => {
      document.documentElement.classList.remove('mf-docs-page');
    };
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = documentTitle;

    const existing = document.querySelector('link[data-mf-integration-guide-font]');
    let link = existing;
    let addedLink = false;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = DOCS_FONTS_HREF;
      link.setAttribute('data-mf-integration-guide-font', 'true');
      document.head.appendChild(link);
      addedLink = true;
    }

    return () => {
      document.title = previousTitle;
      if (addedLink && link?.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [documentTitle]);

  useEffect(() => {
    if (!navOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeNav();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen, closeNav]);

  useEffect(() => {
    if (!navOpen) return undefined;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const previous = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      htmlOverflow: documentElement.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      body.style.overflow = previous.bodyOverflow;
      documentElement.style.overflow = previous.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [navOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_NAV_MQ);
    const onChange = () => {
      if (!mediaQuery.matches) closeNav();
    };

    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, [closeNav]);

  useEffect(() => {
    const main = document.querySelector('.mf-integration-guide .docs-main');
    if (!main) return undefined;

    const usedIds = new Set(
      Array.from(document.querySelectorAll('[id]'), (el) => el.id),
    );

    const slugify = (value) =>
      (value || '')
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

    const headings = Array.from(main.querySelectorAll('h2, h3'));
    const createdAnchors = [];

    headings.forEach((heading) => {
      if (!heading.id) {
        const base = slugify(heading.textContent) || 'section';
        let id = base;
        let n = 2;
        while (usedIds.has(id)) id = `${base}-${n++}`;
        usedIds.add(id);
        heading.id = id;
      }
      heading.classList.add('docs-heading');
      heading.style.scrollMarginTop = `${SCROLL_SPY_OFFSET}px`;

      if (heading.querySelector('.docs-heading-anchor')) return;

      const label = (heading.textContent || 'section').trim();
      const anchor = document.createElement('a');
      anchor.className = 'docs-heading-anchor';
      anchor.href = `#${heading.id}`;
      anchor.setAttribute('aria-label', `Copy link to “${label}”`);
      anchor.innerHTML = '<span aria-hidden="true">#</span>';

      const onClick = (event) => {
        event.preventDefault();
        const url = `${window.location.origin}${window.location.pathname}#${heading.id}`;
        window.history.pushState(null, '', `#${heading.id}`);
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (navigator.clipboard?.writeText) {
          navigator.clipboard
            .writeText(url)
            .then(() => {
              anchor.classList.add('is-copied');
              window.setTimeout(() => anchor.classList.remove('is-copied'), 1600);
            })
            .catch(() => {});
        }
      };

      anchor.addEventListener('click', onClick);
      heading.appendChild(anchor);
      createdAnchors.push({ anchor, onClick });
    });

    const candidates = navSectionIds
      .map((id) => {
        const el = document.getElementById(id);
        return el ? { el, navId: id } : null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        const pos = a.el.compareDocumentPosition(b.el);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });

    let frame = 0;
    const pickActive = () => {
      let current = null;
      candidates.forEach(({ el, navId }) => {
        if (el.getBoundingClientRect().top <= SCROLL_SPY_OFFSET) {
          current = navId;
        }
      });
      setActiveSection((prev) => (prev === current ? prev : current));
    };
    const schedulePick = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        pickActive();
      });
    };

    if (candidates.length) {
      window.addEventListener('scroll', schedulePick, { passive: true });
      window.addEventListener('resize', schedulePick);
      window.addEventListener('hashchange', pickActive);
      pickActive();
    }

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedulePick);
      window.removeEventListener('resize', schedulePick);
      window.removeEventListener('hashchange', pickActive);
      createdAnchors.forEach(({ anchor, onClick }) => {
        anchor.removeEventListener('click', onClick);
        anchor.remove();
      });
    };
  }, [navSectionIds]);

  const wordmarkSrc = theme === 'dark' ? wordmarkWhite : wordmarkBlack;

  return (
    <div
      className={`mf-integration-guide${navOpen ? ' docs-nav-open' : ''}${
        showNav ? '' : ' docs-no-nav'
      }`}
      data-mf-resolved={theme}
    >
      <header className="docs-topbar">
        <div className="docs-topbar-inner">
          <div className="docs-topbar-start">
            <button type="button" className="docs-topbar-brand" onClick={scrollToTop}>
              <img
                src={wordmarkSrc}
                alt="MagicFurnish"
                className="docs-topbar-wordmark-img"
              />
            </button>
          </div>
          <div className="docs-topbar-end">
            <div className="docs-theme-switch" role="group" aria-label="Color theme">
              <button
                type="button"
                className="docs-theme-seg"
                data-active={theme === 'light'}
                aria-pressed={theme === 'light'}
                aria-label="Light mode"
                title="Light mode"
                onClick={() => setTheme('light')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="4.2" />
                  <path d="M12 2.5v2.4M12 19.1v2.4M4.2 4.2l1.7 1.7M18.1 18.1l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.2 19.8l1.7-1.7M18.1 5.9l1.7-1.7" />
                </svg>
              </button>
              <button
                type="button"
                className="docs-theme-seg"
                data-active={theme === 'dark'}
                aria-pressed={theme === 'dark'}
                aria-label="Dark mode"
                title="Dark mode"
                onClick={() => setTheme('dark')}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 13.2A8.2 8.2 0 1 1 10.8 4a6.4 6.4 0 0 0 9.2 9.2z" />
                </svg>
              </button>
            </div>
            {showNav ? (
              <button
                type="button"
                className={`docs-menu-toggle${navOpen ? ' is-open' : ''}`}
                aria-expanded={navOpen}
                aria-controls="docs-nav-drawer"
                onClick={() => setNavOpen((open) => !open)}
              >
                <span className="docs-menu-icon" aria-hidden="true" />
                <span className="docs-menu-label">{navOpen ? 'Close menu' : 'Open menu'}</span>
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {showNav ? (
        <button
          type="button"
          className="docs-nav-backdrop"
          aria-label="Close navigation"
          aria-hidden={!navOpen}
          tabIndex={navOpen ? 0 : -1}
          onClick={closeNav}
        />
      ) : null}

      <div className="docs-shell">
        {showNav ? (
          <aside
            className={`docs-sidebar${navOpen ? ' is-open' : ''}`}
            id="docs-nav-drawer"
            aria-label="Documentation sections"
          >
            <nav className="docs-nav">
              {navGroups.map((group) => (
                <div
                  key={group.heading}
                  className={`docs-nav-group${group.primary ? ' docs-nav-group--primary' : ''}`}
                >
                  <p className="docs-nav-group-heading">{group.heading}</p>
                  <ul>
                    {group.items.map(({ id, label }) => (
                      <li key={id}>
                        <a
                          href={`#${id}`}
                          className={activeSection === id ? 'is-active' : undefined}
                          aria-current={activeSection === id ? 'location' : undefined}
                          onClick={closeNav}
                        >
                          {label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>
        ) : null}

        <div className="wrap docs-main">
          {children}

          <footer className="foot">
            <div className="foot-row">
              <div className="foot-meta">
                <span>
                  Questions? <a href={`mailto:${DOCS_SUPPORT_EMAIL}`}>{DOCS_SUPPORT_EMAIL}</a>
                </span>
                <span className="foot-updated">Last updated {lastUpdated}</span>
              </div>
              <img src={wordmarkSrc} alt="" className="docs-foot-wordmark" aria-hidden="true" />
            </div>
            <nav className="foot-legal" aria-label="Legal">
              <a href={MF_TERMS_URL} target="_blank" rel="noopener noreferrer">
                Terms
              </a>
              <span className="foot-legal-sep" aria-hidden="true">
                ·
              </span>
              <a href={MF_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
              <span className="foot-legal-sep" aria-hidden="true">
                ·
              </span>
              <a href={MF_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                Support
              </a>
              <span className="foot-legal-sep" aria-hidden="true">
                ·
              </span>
              <span className="foot-version">{APP_VERSION}</span>
            </nav>
          </footer>
        </div>
      </div>
    </div>
  );
};

DocsGuide.propTypes = {
  documentTitle: PropTypes.string.isRequired,
  navGroups: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      primary: PropTypes.bool,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
  lastUpdated: PropTypes.string.isRequired,
  showNav: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default DocsGuide;
